import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import _ from "lodash";

// Contexts
import { BundleContext } from "../../context/BundleProvider";
import { AuthContext } from "../../context/AuthProvider";
import { ThemeContext } from "../../context/ThemeProvider";
import { useAnalytics } from "../../context/AnalyticsProvider";

// Components
import {
  FlashcardMenuItem,
  ProgressModal,
  EmptyFlashcardsState
} from "../../components/flashcards";
import CommonHeader from "../../components/common/CommonHeader";
import StatsGrid from "../../components/common/StatsGrid";
import { faBook, faLayerGroup, faBookmark, faChartLine, faCheckCircle, faStar, faLock } from "@fortawesome/free-solid-svg-icons";
import PremiumDialog from "../../components/common/PremiumDialog";

// Constants
import { GET_FLASHCARDS } from "../../api/content";
import { GET_APP_DOMAIN_LIST, GET_FLASH_CARD_MENU } from "../../../app_constant";
import { STRINGS } from "../../constant";

let _flashcardsTracked = false;

const FlashCards = () => {
  const navigate = useNavigate();
  const { selectedCertificate } = useContext(BundleContext);
  const { profile, isSubscribe } = useContext(AuthContext);
  const { theme } = useContext(ThemeContext);
  const { track, trackStudySessionStart } = useAnalytics();

  // State management
  const [expandedMenus, setExpandedMenus] = useState(['by_topic']);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedMainMenu, setSelectedMainMenu] = useState(null);
  const [progressModalConfig, setProgressModalConfig] = useState({
    isOpen: false,
    type: null,
    title: STRINGS.flashcards,
    message: STRINGS.flashcard_message
  });
  const [showPremiumDialog, setShowPremiumDialog] = useState(false);

  // Get flashcards and menu data
  const FLASHCARDS = GET_FLASHCARDS(selectedCertificate);
  let FLASH_CARD_MENU = GET_FLASH_CARD_MENU(selectedCertificate);

  // Track flashcards list view (once per page load)
  useEffect(() => {
    if (_flashcardsTracked) return;
    _flashcardsTracked = true;
    track('flashcard_menu_viewed', {
      sets_available: FLASH_CARD_MENU?.length || 0,
      total_cards: FLASHCARDS?.length || 0
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter out "By Topics (Free Plan)" menu item completely for all users
  FLASH_CARD_MENU = FLASH_CARD_MENU.filter(item => item.key !== 'by_topic_free_model');

  // Calculate statistics
  const calculateStats = () => {
    const bookmarkedCards = profile?.flashcard_bookmarks || [];
    const progress = profile?.flashcards_progress || [];
    
    const totalFlashcards = FLASHCARDS?.length || 0;
    const totalBookmarks = bookmarkedCards.length;
    
    // Calculate total free flashcards for non-subscribed users
    let totalFreeFlashcards = 0;
    if (!isSubscribe) {
      const APP_DOMAIN_LIST = GET_APP_DOMAIN_LIST(selectedCertificate);
      APP_DOMAIN_LIST.forEach(domain => {
        totalFreeFlashcards += domain.num_flashcards || 0;
      });
    }

    // Count exam essentials
    const examEssentials = FLASHCARDS.filter(c => c.topic_id === 200).length;

    const statsData = [
      {
        icon: faLayerGroup,
        stat: totalFlashcards.toLocaleString(),
        label: 'Total Cards',
        color: theme?.primary?.orange?.[500],
        iconType: 'fontawesome'
      },
    ];

    // Add Free Cards stat for non-subscribed users, otherwise add Exam Essentials
    if (!isSubscribe && totalFreeFlashcards > 0) {
      statsData.push({
        icon: faLock,
        stat: totalFreeFlashcards.toLocaleString(),
        label: 'Free Cards',
        color: theme?.primary?.green?.[500],
        iconType: 'fontawesome',
      });
    } else {
      statsData.push({
        icon: faStar,
        stat: examEssentials,
        label: 'Exam Essentials',
        color: theme?.primary?.orange?.[500],
        iconType: 'fontawesome'
      });
    }

    statsData.push({
      icon: faBookmark,
      stat: totalBookmarks,
      label: 'Bookmarked',
      color: theme?.primary?.orange?.[500],
      iconType: 'fontawesome'
    });

    return statsData;
  };

  const stats = calculateStats();

  function getDomainName(item) {
    const APP_DOMAIN_LIST = GET_APP_DOMAIN_LIST(selectedCertificate);
    const array = APP_DOMAIN_LIST.find((e) => e.id == item?.topic_id);
    return array?.domain_name ?? "";
  }

  // Get flashcard count for menu items
  const getFlashcardCount = (item, menuType) => {
    if (menuType === "bookmark") {
      const bookmarks = profile?.flashcard_bookmarks || [];
      return FLASHCARDS.filter(c => bookmarks.includes(c.id)).length;
    }
    
    if (menuType === "exam") {
      return item?.topic_ids 
        ? FLASHCARDS.filter(c => item.topic_ids.includes(c.topic_id)).length
        : FLASHCARDS.filter(c => c.type === "Exam Essentials").length;
    }
    
    if (menuType === "all_cards") {
      return FLASHCARDS.length;
    }
    if (menuType === "quick") {
      return 10;
    }
    
    return FLASHCARDS.filter(c => c.topic_id === item.id).length;
  };

  // Check if set has progress
  const hasProgress = (setId) => {
    const progress = profile?.flashcards_progress || [];
    return progress.some(p => p.flashcard_set_id === setId);
  };

  // Get flashcards for navigation
  const getFlashcardsForNavigation = (item, menu) => {
    let cards = [];
    let setId;

    switch (menu.key) {
      case "bookmark":
        const bookmarks = profile?.flashcard_bookmarks || [];
        cards = FLASHCARDS.filter(c => bookmarks.includes(c.id));
        setId = "bookmark";
        break;
        
      case "exam":
        cards = item?.topic_ids 
          ? FLASHCARDS.filter(c => item.topic_ids.includes(c.topic_id))
          : FLASHCARDS.filter(c => c.type === "Exam Essentials");
        setId = "exam";
        break;
        
      case "all_cards":
        cards = FLASHCARDS;
        setId = 1 + 2;
        break;
        
      case "quick":
        cards = _.shuffle(FLASHCARDS).slice(0, 10);
        setId = "quick";
        break;
        
      default:
        cards = FLASHCARDS.filter(c => c.topic_id === item.id);
        setId = item.id + 2;
        
        // Apply free model limit for non-subscribed users clicking on "By Topics"
        if (!isSubscribe && menu.key === "by_topic" && item.num_flashcards) {
          cards = cards.slice(0, item.num_flashcards);
        }
    }

    // Add domain_name to each flashcard
    cards = cards.map(card => ({
      ...card,
      domain_name: getDomainName(card)
    }));

    return { cards, setId };
  };

  // Handle item click
  const handleItemClick = (item, menu) => {
    // For non-subscribed users clicking on "By Topics", allow access to free flashcards
    // Don't redirect to chooseplan - they can access free flashcards
    if (item.requires_subscription && !isSubscribe && menu.free_model !== 1 && menu.key !== "by_topic") {
      navigate('/chooseplan', {
        state: { canSkip: false, source: 'flashcards' }
      });
      return;
    }

    const { cards, setId } = getFlashcardsForNavigation(item, menu);

    // Check for existing progress
    if (hasProgress(setId) && menu.key !== "quick") {
      setSelectedTopic(item);
      setSelectedMainMenu(menu);
      setProgressModalConfig({
        isOpen: true,
        type: menu.key,
        title: STRINGS.flashcards,
        message: STRINGS.flashcard_message
      });
    } else {
      navigateToFlashcards(cards, setId, item.title || menu.title);
    }
  };

  // Handle special menu items
  const handleSpecialMenuItem = (menu) => {
    if (menu.key === "quick") {
      const cards = _.shuffle(FLASHCARDS).slice(0, 10);
      navigateToFlashcards(cards, "quick", menu.title);
    } else {
      handleItemClick(menu, menu);
    }
  };

  // Navigate to flashcards
  const navigateToFlashcards = (cards, setId, title, resume = false) => {
    const navigationState = {
      cards,
      set_id: setId,
      title
    };
    console.log("navigationState", navigationState);

    // Add last index if resuming
    if (resume) {
      const progress = profile?.flashcards_progress || [];
      const setProgress = progress.find(p => p.flashcard_set_id === setId);
      if (setProgress) {
        const lastIndex = cards.findIndex(c => c.id === setProgress.last_seen_flashcard_id);
        navigationState.lastIndex = lastIndex === -1 ? 0 : lastIndex;
      }
    }
    
    // Track flashcard session start
    trackStudySessionStart(title || 'Flashcards', {
      type: 'flashcards',
      card_count: cards?.length || 0,
      is_resume: resume
    });

    navigate('/flashcard/questions', { state: navigationState });
  };

  // Handle progress modal action
  const handleProgressAction = (startOver) => {
    const { cards, setId } = getFlashcardsForNavigation(
      selectedTopic || selectedMainMenu, 
      selectedMainMenu
    );
    
    navigateToFlashcards(
      cards, 
      setId, 
      selectedTopic?.title || selectedMainMenu?.title,
      !startOver
    );
    
    setProgressModalConfig({ ...progressModalConfig, isOpen: false });
    setSelectedTopic(null);
    setSelectedMainMenu(null);
  };

  // Toggle menu expansion
  const toggleMenu = (menuKey) => {
    setExpandedMenus(prev => 
      prev.includes(menuKey) 
        ? prev.filter(key => key !== menuKey)
        : [...prev, menuKey]
    );
  };

  // Handle locked item click
  const handleLockedItemClick = (menu) => {
    setShowPremiumDialog(true);
  };
  // Handle premium upgrade
  const handleUpgrade = () => {
    navigate('/chooseplan');
  };


  return (
    <div className="min-h-screen transition-colors duration-300" 
         style={{ background: theme?.background?.main }}>
      <div>
      <CommonHeader
      title="Flashcards"
      icon={faBook}
      subtitle="Reinforce key concepts with active recall and spaced repetition"
      quote="Memory is the residue of thought. Repetition makes it permanent."
      onBack={() => navigate("/home")}
      theme={theme}
      showBackButton={true}
    />
        <div className="pb-4">
        <StatsGrid stats={stats} theme={theme} />
        </div>
        
        {/* Menu Items */}
        <div className="space-y-4">
          {FLASH_CARD_MENU.map((menu, index) => (
            <FlashcardMenuItem
              key={menu.key || index}
              menu={menu}
              isExpanded={expandedMenus.includes(menu.key)}
              onToggle={() => toggleMenu(menu.key)}
              onItemClick={(item) => handleItemClick(item, menu)}
              onMenuClick={() => handleSpecialMenuItem(menu)}
              getFlashcardCount={(item) => getFlashcardCount(item, menu.key)}
              isSubscribed={isSubscribe}
              theme={theme}
              onLockedItemClick={handleLockedItemClick}
              flashcardsList={FLASHCARDS}
            />
          ))}
        </div>
        
        {/* Empty State */}
        {FLASH_CARD_MENU.length === 0 && (
          <EmptyFlashcardsState theme={theme} />
        )}
      </div>
      
      {/* Progress Modal */}
      <ProgressModal
        isOpen={progressModalConfig.isOpen}
        onClose={() => setProgressModalConfig({ ...progressModalConfig, isOpen: false })}
        onStartOver={() => handleProgressAction(true)}
        onResume={() => handleProgressAction(false)}
        title={progressModalConfig.title}
        message={progressModalConfig.message}
        theme={theme}
      />

<PremiumDialog
        isOpen={showPremiumDialog}
        onClose={() => setShowPremiumDialog(false)}
        onUpgrade={() => {
          setShowPremiumDialog(false);
          handleUpgrade();
        }}
        theme={theme}
      />
    </div>
  );
};

export default FlashCards;